import { type Signal, signal } from '@angular/core';
import { CIRCUIT_PAUSE_MAX_MS, CIRCUIT_PAUSE_MS, CIRCUIT_THRESHOLD } from '../entities/upload-retry-policy';

/** Service-side health is global: one breaker for every queue of the app. Only an accepted upload clears the streak. */
export class UploadCircuitBreaker {
  private failures = 0;
  private trips = 0;
  private pausedUntil: number | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly parked = new Map<string, () => void>();
  private readonly state = signal(false);
  readonly paused: Signal<boolean> = this.state.asReadonly();

  /** False = the pump must stop; `wake` runs when the pause ends. */
  admit(parkKey: string, wake: () => void): boolean {
    if (this.pausedUntil === null) return true;
    const remaining = this.pausedUntil - Date.now();
    if (remaining > 0) {
      this.parked.set(parkKey, wake);
      this.schedule(remaining);
      return false;
    }

    this.close();
    return true;
  }

  recordFailure(parkKey: string, wake: () => void): void {
    this.failures++;
    if (this.failures < CIRCUIT_THRESHOLD) return;

    this.failures = 0;
    this.trips++;
    const pause = Math.min(CIRCUIT_PAUSE_MS * 2 ** (this.trips - 1), CIRCUIT_PAUSE_MAX_MS);
    this.pausedUntil = Date.now() + pause;
    this.state.set(true);
    this.parked.set(parkKey, wake);
    this.schedule(pause);
  }

  recordAccepted(): void {
    this.failures = 0;
    this.trips = 0;
  }

  /** The person asking to try NOW: drop the pause but keep the trip escalation - a still-dead service re-trips longer. */
  resume(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.close();
    this.wakeAll();
  }

  destroy(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.parked.clear();
  }

  private schedule(delayMs: number): void {
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      // RACE-SAFE: a repeat trip may have extended the pause while this timer waited - resume at the new deadline.
      const remaining = this.pausedUntil !== null ? this.pausedUntil - Date.now() : 0;
      if (remaining > 0) {
        this.schedule(remaining);
        return;
      }

      this.close();
      this.wakeAll();
    }, delayMs);
  }

  private close(): void {
    this.pausedUntil = null;
    this.state.set(false);
  }

  private wakeAll(): void {
    const wakes = [...this.parked.values()];
    this.parked.clear();
    for (const wake of wakes) wake();
  }
}
